/**
 * A run, turned into the steps a person reads.
 *
 * This is the shape the screen is built around, and the reason it exists now
 * rather than later: the agent loop that lands here next writes *more kinds of
 * step*, not a different screen. Today one run produces four steps. Later the
 * same list holds "read main.py", "changed two lines", "ran the tests", "the
 * test failed, trying again". Building the panel around a step list from the
 * first day means the loop is an addition instead of a rewrite.
 *
 * The one rule that outranks the rest: **a refusal always becomes a step**.
 * Phase 2b proved we cannot make somebody else's tool print our sentence, pip
 * prints its own retry warnings over the proxy's 403, so the sentence travels
 * on the result and the screen is the thing that has to show it. If this
 * function ever drops it, "no silent failures" is a claim and not a fact.
 */
import { naRondes, stapInTaal } from "./codestappen-taal";

export const STEP_KINDS = ["task", "network", "output", "result"];

/** Steps for one finished run. */
export function stepsForRun(request, result) {
  if (!result) return [];
  const steps = [
    {
      kind: "task",
      id: "task",
      title: request?.title || `${request?.path || "Een bestand"} uitgevoerd`,
      detail: request?.language ? `met ${request.language}` : "",
    },
  ];

  // Before the output, on purpose. A person reading top to bottom meets the
  // reason a download failed before they meet the failure.
  const network = result.network || {};
  if (network.note) {
    steps.push({
      kind: "network",
      id: "network",
      title: "Netwerk geweigerd",
      detail: network.note,
      hosts: network.refused_hosts || [],
    });
  } else if ((network.attempts || []).some((a) => a.decision === "allowed")) {
    steps.push({
      kind: "network",
      id: "network-allowed",
      title: "Netwerk bereikt",
      detail: network.attempts
        .filter((a) => a.decision === "allowed")
        .map((a) => a.host)
        .join(", "),
      hosts: [],
    });
  }

  if (result.stdout || result.stderr) {
    steps.push({
      kind: "output",
      id: "output",
      stdout: result.stdout || "",
      stderr: result.stderr || "",
      truncated: Boolean(result.truncated),
    });
  }

  steps.push({
    kind: "result",
    id: "result",
    ok: result.exit_code === 0 && !result.timed_out,
    title: resultTitle(result),
    detail: `${Math.max(1, Math.round((result.duration_ms || 0) / 100) / 10)}s`,
  });
  return steps;
}

function resultTitle(result) {
  if (result.timed_out) return "Gestopt: de tijdslimiet is verstreken";
  if (result.exit_code === 0) return "Klaar";
  return `Mislukt met foutcode ${result.exit_code}`;
}

/**
 * What to offer when a run was refused the network.
 *
 * Package registries get their own answer because that is the common case and
 * because "allow pypi.org" alone would leave pip broken halfway: the index and
 * the downloads are two different hosts, and a person cannot be expected to
 * know that.
 */
const PACKAGE_HOSTS = ["pypi.org", "files.pythonhosted.org", "registry.npmjs.org"];

export function grantOffer(refusedHosts = []) {
  if (!refusedHosts.length) return null;
  const packagesOnly = refusedHosts.every((h) => PACKAGE_HOSTS.includes(h));
  if (packagesOnly) {
    return {
      mode: "packages",
      label: "Pakketten installeren toestaan",
      explains: "pypi.org, files.pythonhosted.org en registry.npmjs.org, verder niets",
    };
  }
  return {
    mode: "hosts",
    hosts: refusedHosts,
    label: `${refusedHosts.join(", ")} toestaan`,
    explains: "alleen deze, verder niets",
  };
}


/**
 * The agent's own steps, already in the shape this list draws.
 *
 * The backend sends them in the same form a run produces, which is the whole
 * point of having built the panel around a step list before the loop existed:
 * this function maps kinds to how they read, and adds nothing.
 */
const AGENT_TITLES = {
  task: "Taak",
  // GL215 S1 (Buzz J-7): een gereedschap dat stukliep. Elke crash heet crash.
  crash: "Vastgelopen",
  // FIX 6: een stap per ronde, terwijl de agent loopt.
  round: "Ronde",
  memory: "Onthouden",
  write: "Bestand geschreven",
  read: "Bestand gelezen",
  run: "Uitgevoerd",
  // GL223-2: de afloop van een installatie (Installeer of Weiger).
  install: "Installatie",
  // CA8: de agent maakte een issue in de repo van de werkruimte.
  issue: "Issue",
  check: "Uitkomst gecontroleerd",
  problem: "Probleem",
  // Not the same thing as "Stopped", and that difference is why it exists. A
  // run used to read as fine right up to the moment it read as stuck: the
  // block was recorded and the warning that came before it was not, so the
  // struggle was visible only to the model. This is the run saying it noticed
  // itself going round, while it still had somewhere to go.
  struggling: "Draait in kringetjes",
  // Bytes that actually went to somebody else's machine, and this loop was
  // the last of the three to say so. Not the same question as which company
  // answered you: a run that fetched a package sent real data out while the
  // answer stayed local, and only one of those two facts had a line.
  outbound: "Verliet deze machine",
  // LC3 (31 okt 2026): het plan voor de rit, en een toetsrit met unittest in de sandbox.
  plan: "Plan",
  test: "Toetsen",
  // LC4 (31 okt 2026): zoeken in de code van de werkruimte.
  search: "Gezocht in de code",
  // CA1 (31 okt 2026).
  delete: "Verwijderd",
  move: "Verplaatst",
  ask: "Heeft jou nodig",
  done: "Klaar",
  stopped: "Gestopt",
  // Glasronde 0.9.231 punt 3: een leeg antwoord, opnieuw gevraagd een denkstand lager. Een melding, geen probleem.
  herkans: "Opnieuw gevraagd",
};

export function stepsFromAgent(steps = []) {
  return steps.map((step, index) => ({
    ...step,
    id: `agent-${index}`,
    kind: step.kind,
    label: AGENT_TITLES[step.kind] || step.kind,
  }));
}

/** Whether the loop ended in a way that needs the person. */
export function needsYou(outcome) {
  return (
    outcome === "asked" ||
    outcome === "stuck" ||
    outcome === "out of rounds" ||
    outcome === "wrong outcome" ||
    outcome === "crashed"
  );
}

/**
 * The outcome in words, because the internal name is not a sentence.
 *
 * "done, not verified" is the one that matters here. It means the work
 * finished and the check could not be made, and it has to read as exactly
 * that: not as a pass, and not as a failure. A label nobody understands is the
 * same as no label at all.
 */
const OUTCOMES = {
  done: "Klaar, en het resultaat past bij de taak",
  "done, not verified": "Klaar, maar het resultaat is niet gecontroleerd",
  "wrong outcome": "Het resultaat paste niet bij de taak",
  asked: "Wacht op jou",
  stuck: "Gestopt: de agent bleef zichzelf herhalen",
  "out of rounds": "Gestopt: de rondes zijn op",
  "model unavailable": "Het model kon niet antwoorden",
  // De beginwaarde van de lus in agent_loop, en hij ONTBRAK hier tot
  // 21 augustus 2026. De terugval in `outcomeSentence` is
  // `OUTCOMES[outcome] || outcome`, dus een ontbrekende zin gaat niet stuk:
  // hij zet de KALE CODE op het scherm. Dat is de stilste manier waarop dit
  // kan breken, want er staat ineens jargon waar een zin hoorde te staan.
  //
  // GEMETEN: vandaag is deze afloop onbereikbaar. Elke uitgang van de lus
  // overschrijft hem, en `max_rounds` kan via de route geen 0 zijn. Hij staat
  // hier omdat de backend hem WEL kan teruggeven, en omdat een pad dat nooit
  // wordt betreden door geen enkele meting wordt aangewezen (huisregel 7.7).
  // De wachter die telt of ELKE afloopcode een zin heeft, is het echte werk;
  // deze regel is wat die wachter aanwees.
  // PUNT 91. De lus stelt de vraag twee keer per ronde en eindigt NETJES in
  // plaats van met een uitzondering: eis 2 van 3ax-5 zegt dat lopend werk niet
  // half wordt achtergelaten, en een traceback middenin een rit is precies dat.
  // Daarom heeft deze afloop een zin nodig, net als elke andere.
  noodstop: "Gestopt: de noodstop staat aan",
  // POST G: Stop voor deze ene rit, niet de noodstop.
  gestopt: "Gestopt: je drukte op Stop",
  "gave up": "Gestopt zonder resultaat",
  // GL215 S1: een gereedschap liep onverwacht stuk; de stap zegt wat er al gebeurd was.
  crashed: "Vastgelopen: een gereedschap liep stuk",
  // LC3: de toetsen bleven rood na twee pogingen (agent_loop MAX_ROOD_POGINGEN).
  "tests red": "Gestopt: de toetsen bleven rood",
  // Glasronde 0.9.221: de opdracht vroeg om een toets, en na een vraag kwam die er niet.
  "no test": "Gestopt: de opdracht vroeg om een toets en die is er niet",
  // CA7 (besluit Frank 1 nov): een app-opdracht in deelstappen duurt hooguit 60 minuten, ook midden in een rit.
  "tijd op": "Gestopt: het tijdsbudget van de app-opdracht is op",
};

/** FIX 6: de REDEN, als elke ronde op hetzelfde probleem strandde. Franks
 *  glasronde: acht rondes leeg en op het scherm alleen een draaiend rondje.
 *  Een stop zonder reden laat niemand weten wat er anders moet. */
export const GEEN_OPDRACHT_REDEN =
  "Het model gaf in geen enkele ronde een opdracht terug die de agent kan uitvoeren. " +
  "Probeer de taak anders te zeggen, of kies een ander model.";

function reden(steps = []) {
  const problemen = steps.filter((s) => s.kind === "problem");
  if (!problemen.length) return "";
  const rondes = steps.filter((s) => s.kind === "round").length;
  const geenOpdracht = problemen.filter((s) => /geen opdracht terug/.test(s.title || "")).length;
  if (rondes && geenOpdracht >= rondes) return GEEN_OPDRACHT_REDEN;
  return `Laatste probleem: ${problemen[problemen.length - 1].title}`;
}

export function outcomeSentence(outcome, rounds, steps = [], taal = "nl") {
  // GL216 punt 2: in de taal van de app. De Nederlandse zinnen blijven de bron.
  const said = stapInTaal(OUTCOMES[outcome] || outcome, taal);
  const zin = rounds ? `${said}, ${naRondes(rounds, taal)}` : said;
  const waarom = ["done", "done, not verified", "gestopt", "noodstop", "tijd op"].includes(outcome) ? "" : stapInTaal(reden(steps), taal);
  return waarom ? `${zin}. ${waarom}` : zin;
}
